import React from "react";
import { Link } from "react-router-dom";
import { Reveal, Overline } from "@/components/Reveal";
import { PageHero, Section, Button } from "@/components/UI";
import { FileText, BookMarked, Microscope, HelpCircle, Download, ArrowUpRight } from "lucide-react";

const RESOURCES = [
  { icon: FileText, t: "National Standards of Practice", tag: "Document", d: "The full framework — scope, ethics, IPAC, consent, and trauma-informed care." },
  { icon: BookMarked, t: "Code of Ethics", tag: "Document", d: "Professional duties to patients, the public, and the profession." },
  { icon: Microscope, t: "Research & Evidence", tag: "Library", d: "Curated evidence supporting restorative micropigmentation outcomes." },
  { icon: HelpCircle, t: "Patient Guide", tag: "For the public", d: "What to expect, how to prepare, and questions to ask your practitioner." },
];

const FAQ = [
  ["Is restorative micropigmentation the same as cosmetic tattooing?", "No. Restorative paramedical micropigmentation addresses medical and congenital conditions — such as post-mastectomy areola restoration, scar and vitiligo camouflage — and is governed by clinical standards distinct from cosmetic permanent makeup."],
  ["How do I know a practitioner is certified?", "Search the public Practitioner Registry. Every listed practitioner holds current CCPM certification and verified status."],
  ["Does insurance cover restorative micropigmentation?", "Coverage varies by province. CCPM advocates for consistent recognition of restorative work as part of reconstructive care."],
  ["How do I file a complaint?", "Use the Contact page and select 'Public Complaint'. All complaints are reviewed confidentially by the Standards & Ethics Committee."],
];

export default function Resources() {
  return (
    <>
      <PageHero overline="Resources" testId="resources-hero"
        title="Standards, research, and guidance in one place"
        subtitle="A growing library of documentation for practitioners, healthcare professionals, and the public — as our national standards move through consultation and adoption." />

      <Section className="py-20 lg:py-28">
        <div className="grid md:grid-cols-2 gap-px bg-ccpm-border border border-ccpm-border">
          {RESOURCES.map((r, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08} className="bg-ccpm-ivory">
              <div className="group p-8 lg:p-10 h-full flex flex-col" data-testid={`resource-${i}`}>
                <div className="flex items-start justify-between">
                  <r.icon strokeWidth={1.2} size={30} className="text-ccpm-olive" />
                  <span className="overline text-ccpm-taupe">{r.tag}</span>
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl text-ccpm-charcoal mt-6 group-hover:text-ccpm-olive transition-colors">{r.t}</h3>
                <p className="mt-3 text-ccpm-taupe font-light leading-relaxed flex-1">{r.d}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ccpm-olive"><Download size={15} /> Available on request</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <div className="bg-ccpm-stone py-20 lg:py-28">
        <Section>
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4"><Reveal><Overline>Frequently Asked</Overline><h2 className="font-serif text-4xl text-ccpm-charcoal mt-4 leading-tight">Answers for patients & practitioners</h2></Reveal></div>
            <div className="lg:col-span-8">
              {FAQ.map(([q, a], i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="border-t border-ccpm-border py-7">
                    <h3 className="font-serif text-xl lg:text-2xl text-ccpm-charcoal">{q}</h3>
                    <p className="mt-3 text-ccpm-taupe font-light leading-relaxed">{a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>
      </div>

      <Section className="py-20"><Reveal><Link className="group flex flex-wrap items-center justify-between gap-6 border border-ccpm-border p-10 hover:border-ccpm-olive transition-colors" to="/contact">
        <div><h2 className="font-serif text-3xl text-ccpm-charcoal">Can't find what you need?</h2><p className="mt-2 text-ccpm-taupe font-light">Our office can provide documentation and answer specific enquiries.</p></div>
        <ArrowUpRight size={32} className="text-ccpm-olive group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
      </Link></Reveal></Section>
    </>
  );
}
